import { useEffect, useMemo, useState } from "react";
import { errorMessage } from "../api/client";
import { usePaymentsSummary } from "../api/payments";
import type { PaymentUserRow } from "../api/types";
import PaymentStatementDrawer from "../components/PaymentStatementDrawer";
import RegisterPaymentDialog from "../components/RegisterPaymentDialog";
import SensitiveToggle, { useSensitiveMask } from "../components/SensitiveToggle";
import { maskPerson } from "../lib/mask";
import {
  balanceState,
  formatBalance,
  formatDateTime,
  initials,
  methodLabel,
  userLabel,
} from "../lib/payments";

type Filter = "all" | "pending" | "settled";

function BalancePill({ u }: { u: PaymentUserRow }) {
  switch (balanceState(u)) {
    case "settled":
      return <span className="bal-pill green">Settled</span>;
    case "overpaid":
      return <span className="bal-pill red">Overpaid {formatBalance(-u.pending_ms)}</span>;
    case "under_hour":
      return (
        <>
          <span className="bal-pill amber">{formatBalance(u.pending_ms)}</span>
          <div className="small muted">under 1h, not payable yet</div>
        </>
      );
    default:
      return <span className="bal-pill amber">{formatBalance(u.pending_ms)}</span>;
  }
}

function toCsv(rows: PaymentUserRow[]): string {
  const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = ["name", "email", "uid", "approved_recordings", "approved_hours", "paid_hours", "pending_hours", "payable_hours", "last_payment_at"];
  const lines = rows.map((u) =>
    [
      u.display_name, u.email, u.uid, u.approved_count,
      (u.approved_ms / 3_600_000).toFixed(2), u.paid_hours,
      (u.pending_ms / 3_600_000).toFixed(2), u.payable_hours, u.last_payment?.paid_at ?? "",
    ].map(esc).join(","),
  );
  return [head.join(","), ...lines].join("\n");
}

export default function PaymentsPage() {
  const summary = usePaymentsSummary();
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");
  const [statementUid, setStatementUid] = useState<string | null>(null);
  const [payUser, setPayUser] = useState<PaymentUserRow | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const mask = useSensitiveMask();
  // what the screen shows: identity masked in demo mode (uid kept for keys and API calls)
  const shown = (u: PaymentUserRow) => (mask.masked ? maskPerson(u) : u);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const rows = useMemo(() => {
    let list = summary.data?.users ?? [];
    if (filter === "pending") list = list.filter((u) => u.pending_ms > 0);
    if (filter === "settled") list = list.filter((u) => u.pending_ms <= 0);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((u) => `${u.display_name ?? ""} ${u.email ?? ""} ${u.uid}`.toLowerCase().includes(q));
    return list;
  }, [summary.data, filter, query]);

  function exportCsv() {
    if (!summary.data) return;
    const blob = new Blob([toCsv(summary.data.users.map(shown))], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `payments-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const totals = summary.data?.totals;

  return (
    <div className="payments-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Payments</h1>
          <p className="page-sub">
            Approved recording time per user and what is still owed. Payments are registered in
            whole hours; leftover minutes carry over until they add up to an hour.
          </p>
        </div>
        <div className="page-head-actions">
          <button className="btn" onClick={exportCsv} disabled={!summary.data}>
            Export CSV
          </button>
        </div>
      </div>

      {summary.isPending && <p className="muted small">Loading…</p>}
      {summary.isError && <p className="error">Failed to load: {errorMessage(summary.error)}</p>}

      {totals && (
        <div className="kpi-row pay-stats">
          <div className="kpi-tile">
            <span className="kpi-label">Approved time</span>
            <span className="kpi-value">{formatBalance(totals.approved_ms)}</span>
            <span className="kpi-sub">{totals.users} users</span>
          </div>
          <div className="kpi-tile">
            <span className="kpi-label">Paid</span>
            <span className="kpi-value green">{totals.paid_hours}h</span>
            <span className="kpi-sub">whole hours registered</span>
          </div>
          <div className="kpi-tile">
            <span className="kpi-label">Pending payment</span>
            <span className={`kpi-value ${totals.pending_ms < 0 ? "red" : "amber"}`}>
              {formatBalance(totals.pending_ms)}
            </span>
            <span className="kpi-sub">
              {totals.payable_hours}h payable in whole hours · {totals.users_waiting} users waiting
            </span>
          </div>
        </div>
      )}

      {summary.data && (
        <section className="card">
          <div className="card-head pay-card-head">
            <h3>
              Users <span className="muted">({rows.length})</span>
            </h3>
            <div className="pay-tools">
              <SensitiveToggle mask={mask} />
              <div className="seg-control" role="tablist" aria-label="Filter">
                {(["all", "pending", "settled"] as Filter[]).map((f) => (
                  <button
                    key={f}
                    className={filter === f ? "seg-btn active" : "seg-btn"}
                    onClick={() => setFilter(f)}
                  >
                    {f === "all" ? "All" : f === "pending" ? "With pending" : "Settled"}
                  </button>
                ))}
              </div>
              <input
                className="text-input pay-search"
                placeholder="Search by name or email"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>

          {rows.length === 0 ? (
            <p className="muted small pay-empty">No users match.</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table pay-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th className="r">Approved</th>
                    <th className="r">Paid</th>
                    <th className="r">Pending ↓</th>
                    <th>Last payment</th>
                    <th className="r">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => {
                    const pct = u.approved_ms > 0 ? Math.min(100, Math.round((u.paid_hours * 3_600_000) / u.approved_ms * 100)) : 0;
                    const last = u.last_payment;
                    const person = shown(u);
                    return (
                      <tr key={u.uid}>
                        <td>
                          <div className="pay-user">
                            <div className="pay-avatar">{initials(person)}</div>
                            <div>
                              <div className="pay-user-name">
                                {userLabel(person)}
                                {!u.is_active && <span className="bal-pill gray">deleted account</span>}
                              </div>
                              <div className="small muted">{person.email ?? person.uid}</div>
                            </div>
                          </div>
                        </td>
                        <td className="r num">
                          {formatBalance(u.approved_ms)}
                          <div className="small muted">{u.approved_count} recordings</div>
                        </td>
                        <td className="r num">
                          {u.paid_hours}h
                          <div className="pay-bar"><i style={{ width: `${pct}%` }} /></div>
                        </td>
                        <td className="r num pay-pending">
                          <BalancePill u={u} />
                        </td>
                        <td>
                          {last ? (
                            <>
                              <div>{formatDateTime(last.paid_at)}</div>
                              <div className="small muted">
                                {last.hours}h
                                {methodLabel(last.method) && (
                                  <span className="pay-method">{methodLabel(last.method)}</span>
                                )}{" "}
                                · by {last.registered_by}
                              </div>
                            </>
                          ) : (
                            <span className="faint">No payments yet</span>
                          )}
                        </td>
                        <td className="r">
                          <div className="pay-actions">
                            <button className="btn small" onClick={() => setStatementUid(u.uid)}>
                              Statement
                            </button>
                            <button
                              className="btn small primary"
                              disabled={u.payable_hours < 1}
                              title={u.payable_hours < 1 ? "Less than one whole hour pending" : "Register whole hours paid"}
                              onClick={() => setPayUser(person)}
                            >
                              Register payment
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <PaymentStatementDrawer
        uid={statementUid}
        onClose={() => setStatementUid(null)}
        onRegister={(u) => setPayUser(u) /* the drawer hands over the row as it shows it */}
        masked={mask.masked}
      />
      <RegisterPaymentDialog user={payUser} onClose={() => setPayUser(null)} onSaved={setToast} />
      <div className={toast ? "toast show" : "toast"} role="status">{toast}</div>
    </div>
  );
}
